import { useState, useEffect } from 'react';
import { api } from '../api/client';

interface TreeItem { name: string; path: string; type: 'file' | 'folder'; size?: number; is_indexed?: boolean; }
interface Project { id: string; name: string; description: string; tech_stack: string[]; vector_count: number; file_count: number; }

export function CodeRepositoriesPage() {
  const [projects, setProjects] = useState<Project[]>([]);
  const [selected, setSelected] = useState<Project | null>(null);
  const [tree, setTree] = useState<TreeItem[]>([]);
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const [fileContent, setFileContent] = useState<{ path: string; content: string; language: string } | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.listProjects().then(d => {
      setProjects(d.projects ?? []);
      setLoading(false);
    }).catch(() => setLoading(false));
  }, []);

  const selectProject = async (p: Project) => {
    setSelected(p);
    setTree([]);
    setFileContent(null);
    const data = await api.getProjectTree(p.id);
    setTree(data.items ?? []);
  };

  const expandFolder = async (item: TreeItem) => {
    if (item.type !== 'folder') return;
    if (expanded.has(item.path)) {
      setExpanded(e => { const n = new Set(e); n.delete(item.path); return n; });
      return;
    }
    setExpanded(e => new Set([...e, item.path]));
    if (!selected) return;
    const data = await api.getProjectTree(selected.id, item.path);
    const children = (data.items ?? []).map((c: TreeItem) => ({ ...c, _parent: item.path }));
    setTree(t => {
      const idx = t.findIndex(x => x.path === item.path);
      return [...t.slice(0, idx + 1), ...children, ...t.slice(idx + 1).filter(x => !(x as any)._parent?.startsWith(item.path))];
    });
  };

  const openFile = async (item: TreeItem) => {
    if (item.type !== 'file' || !selected) return;
    const data = await api.getFileContent(selected.id, item.path);
    setFileContent(data);
  };

  if (loading) return <div style={{ display: 'flex', justifyContent: 'center', padding: '60px' }}><div className="spinner" /></div>;

  return (
    <div>
      <div className="page-header" style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: '12px' }}>
        <div>
          <h1 className="page-title">Code Repositories</h1>
          <p className="page-subtitle">RAG-indexed source code on Cloudflare R2</p>
        </div>
      </div>

      {!projects.length ? (
        <div className="card" style={{ textAlign: 'center', padding: '60px' }}>
          <div style={{ fontSize: '40px', marginBottom: '16px' }}>🗂️</div>
          <div style={{ color: 'var(--text-secondary)', fontSize: '14px' }}>No code repositories indexed yet.</div>
          <div style={{ color: 'var(--text-muted)', fontSize: '12px', marginTop: '8px' }}>RAG indexing happens in background.</div>
        </div>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: '260px 1fr', gap: '16px', height: 'calc(100vh - 200px)' }}>

          {/* Project list */}
          <div style={{ overflow: 'auto' }}>
            {projects.map(p => (
              <div
                key={p.id}
                className={`card ${selected?.id === p.id ? 'card--accent' : ''}`}
                style={{ cursor: 'pointer', marginBottom: '8px' }}
                onClick={() => selectProject(p)}
              >
                <div style={{ fontWeight: 600, fontSize: '13px', marginBottom: '4px' }}>{p.name}</div>
                <div style={{ fontSize: '11px', color: 'var(--text-muted)', marginBottom: '8px' }}>{p.description}</div>
                <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                  <span className="badge badge--purple">{p.vector_count} vectors</span>
                  <span className="badge badge--blue">{p.file_count} files</span>
                  {(p.tech_stack ?? []).slice(0, 2).map(t => (
                    <span key={t} className="badge badge--gray">{t}</span>
                  ))}
                </div>
              </div>
            ))}
          </div>

          {/* File tree + viewer */}
          {selected ? (
            <div style={{ display: 'grid', gridTemplateColumns: '220px 1fr', gap: '12px', overflow: 'hidden' }}>
              {/* Tree */}
              <div className="card" style={{ overflow: 'auto', padding: '12px' }}>
                <div style={{ fontSize: '11px', color: 'var(--text-muted)', marginBottom: '8px', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                  Files
                </div>
                <div className="file-tree">
                  {tree.map(item => (
                    <div
                      key={item.path}
                      className={`tree-item ${fileContent?.path === item.path ? 'selected' : ''}`}
                      style={{ paddingLeft: `${((item as any)._parent?.split('/').length ?? 0) * 12}px` }}
                      onClick={() => item.type === 'folder' ? expandFolder(item) : openFile(item)}
                    >
                      <span className="tree-item__icon">
                        {item.type === 'folder' ? (expanded.has(item.path) ? '📂' : '📁') : getFileIcon(item.name)}
                      </span>
                      <span className="tree-item__name">{item.name}</span>
                      {item.type === 'file' && (
                        <span className="tree-item__badge">
                          {item.is_indexed
                            ? <span title="RAG indexed" style={{ color: 'var(--accent-green)', fontSize: '9px' }}>●</span>
                            : <span title="Not indexed" style={{ color: 'var(--text-muted)', fontSize: '9px' }}>○</span>
                          }
                        </span>
                      )}
                    </div>
                  ))}
                </div>
              </div>

              {/* File viewer */}
              <div className="card" style={{ overflow: 'auto', padding: 0 }}>
                {fileContent ? (
                  <>
                    <div style={{ padding: '10px 16px', borderBottom: '1px solid var(--border)', display: 'flex', alignItems: 'center', gap: '10px' }}>
                      <span style={{ fontFamily: 'var(--font-mono)', fontSize: '12px', color: 'var(--text-secondary)' }}>{fileContent.path}</span>
                      <span className="badge badge--gray">{fileContent.language}</span>
                    </div>
                    <pre style={{
                      margin: 0,
                      padding: '16px',
                      fontFamily: 'var(--font-mono)',
                      fontSize: '12px',
                      lineHeight: '1.7',
                      color: 'var(--text-primary)',
                      overflow: 'auto',
                      whiteSpace: 'pre-wrap',
                      wordBreak: 'break-word',
                    }}>
                      {fileContent.content}
                    </pre>
                  </>
                ) : (
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', color: 'var(--text-muted)', fontSize: '13px' }}>
                    Select a file to view
                  </div>
                )}
              </div>
            </div>
          ) : (
            <div className="card" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--text-muted)' }}>
              Select a project to browse files
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function getFileIcon(name: string): string {
  const ext = name.split('.').pop()?.toLowerCase() ?? '';
  const icons: Record<string, string> = {
    ts: '🔷', tsx: '🔷', js: '🟡', jsx: '🟡', py: '🐍',
    go: '🐹', rs: '🦀', json: '{}', md: '📝', css: '🎨',
    html: '🌐', sql: '🗄', sh: '⚡', yaml: '📋', yml: '📋',
  };
  return icons[ext] ?? '📄';
}
